/**
 * Provenance Types — Mirrors the Analytics API response models exactly.
 *
 * The UI MUST NOT compute verification status — it comes from the server.
 * These types are the single source of truth for all provenance components.
 */

/**
 * Canonical UI trust-state taxonomy. Drives every shield, attestation, tooltip,
 * drawer header, KPI, test, and analytics event across all plugins.
 *
 * - verified: Full provenance chain validates
 * - partial: Some bands missing, no tamper signal
 * - pending: Receipt exists, summary not ready yet
 * - missing: Action has no linked proof chain
 * - failed: Hash/signature/order mismatch detected
 * - restricted: User lacks permission to view detail
 * - not_applicable: Non-tool event, heartbeat, status update
 */
export type ProvenanceStatus =
  | 'verified'
  | 'partial'
  | 'pending'
  | 'missing'
  | 'failed'
  | 'restricted'
  | 'not_applicable';

/**
 * Server-returned chain status from the Analytics API.
 * Mapped to ProvenanceStatus at the UI layer with additional context.
 */
export type ChainStatus =
  | 'verified'
  | 'pending'
  | 'partial'
  | 'broken'
  | 'unverifiable';

export type ProvenanceBandState =
  | 'verified'
  | 'not_applicable'
  | 'pending'
  | 'missing'
  | 'broken'
  | 'redacted'
  | 'unverifiable';

export type Severity = 'success' | 'info' | 'warning' | 'critical';
export type BandSeverity = 'none' | 'info' | 'warning' | 'critical';

export type BandName =
  | 'principal'
  | 'generator'
  | 'authorization'
  | 'approval'
  | 'credential'
  | 'executor'
  | 'downstream';

export interface ProvenanceBand {
  band: BandName;
  label: string;
  state: ProvenanceBandState;
  display_value: string;
  explanation: string;
  detail: Record<string, unknown>;
  severity: BandSeverity;
}

export interface ProvenanceReceipt {
  receipt_type: string;
  outcome: string;
  receipt_hash: string;
  payload_hash: string;
  prev_hash: string;
  jws_preview: string;
  signed_at: string;
}

export interface ChainVerification {
  server_verified: boolean;
  custody_signature_valid: boolean;
  execution_signature_valid: boolean;
  chain_intact: boolean;
  evidence_hash_matches: boolean;
  temporal_order_valid: boolean;
  tenant_ids_consistent: boolean;
  action_ids_consistent: boolean;
  verified_at: string;
}

export interface ChainProof {
  custody_receipt_hash: string;
  execution_receipt_hash: string;
  evidence_pack_hash: string;
  proof_bundle_id: string;
}

export interface ChainVerdict {
  title: string;
  summary: string;
  severity: Severity;
}

export interface ActionProvenanceChain {
  action_id: string;
  tenant_id: string;
  status: ChainStatus;
  verdict: ChainVerdict;
  bands: ProvenanceBand[];
  receipts: ProvenanceReceipt[];
  proof: ChainProof;
  verification: ChainVerification;
}

/**
 * Lightweight summary returned by the batch summary endpoint.
 * Used by feeds and lists to render InlineProvenanceAttestation without
 * fetching the full chain detail.
 */
export interface ProvenanceSummary {
  action_id: string;
  status: ProvenanceStatus;
  verified_band_count: number;
  total_band_count: number;
  risk_level?: 'low' | 'medium' | 'high' | 'critical';
  last_verified_at?: string;
  failure_reason?: string;
  missing_bands?: BandName[];
  redacted: boolean;
}

/**
 * Maps server ChainStatus + contextual information to the canonical
 * ProvenanceStatus used by all UI components.
 *
 * Handles eventual consistency: when a receipt exists (hasActionId=true) but
 * the chain status hasn't propagated from analytics yet (chainStatus=null),
 * the UI shows 'pending' rather than 'missing'.
 */
export function resolveProvenanceStatus(
  chainStatus: ChainStatus | null,
  context: { hasActionId: boolean; isToolEvent: boolean; isRestricted: boolean; hasReceipt?: boolean }
): ProvenanceStatus {
  if (context.isRestricted) return 'restricted';
  if (!context.isToolEvent) return 'not_applicable';
  if (!context.hasActionId) return 'missing';
  if (chainStatus === null) return 'pending';
  switch (chainStatus) {
    case 'verified': return 'verified';
    case 'partial': return 'partial';
    case 'pending': return 'pending';
    case 'broken': return 'failed';
    case 'unverifiable': return 'failed';
  }
}

export interface ProvenanceBandSummary {
  band: BandName;
  state: ProvenanceBandState;
  label: string;
  shortValue: string;
  severity: BandSeverity;
}

/**
 * Copy matrix for user-facing provenance status text.
 * "Custody bands" is internal technical language.
 * User-facing copy uses "chain," "evidence," and "checks."
 */
export const PROVENANCE_COPY: Record<ProvenanceStatus, {
  inline: string;
  tooltip: string;
  drawerTitle: string;
  userAction: string;
}> = {
  verified: {
    inline: 'Chain verified',
    tooltip: 'This action has a verified chain of custody across identity, delegation, policy, command, execution, receipt, and storage evidence.',
    drawerTitle: 'Chain of custody \u2014 Verified',
    userAction: 'Open details',
  },
  partial: {
    inline: 'Evidence incomplete',
    tooltip: 'Some evidence bands are missing. No tampering detected.',
    drawerTitle: 'Chain of custody \u2014 Incomplete',
    userAction: 'Review missing evidence',
  },
  pending: {
    inline: 'Verification pending',
    tooltip: "This action\u2019s provenance chain is being verified.",
    drawerTitle: 'Chain of custody \u2014 Pending',
    userAction: 'Wait / retry',
  },
  missing: {
    inline: 'No provenance',
    tooltip: 'This action has no linked proof chain.',
    drawerTitle: 'Chain of custody \u2014 Not available',
    userAction: 'Explain why',
  },
  failed: {
    inline: 'Verification failed',
    tooltip: 'Hash, signature, or ordering mismatch detected in evidence chain.',
    drawerTitle: 'Chain of custody \u2014 Failed',
    userAction: 'Escalate',
  },
  restricted: {
    inline: 'Restricted',
    tooltip: 'You do not have permission to view provenance details.',
    drawerTitle: 'Chain of custody \u2014 Access restricted',
    userAction: 'Request access',
  },
  not_applicable: {
    inline: '',
    tooltip: '',
    drawerTitle: '',
    userAction: '',
  },
};

/**
 * Maps ProvenanceStatus to visual severity for consistent rendering.
 */
export function provenanceStatusSeverity(status: ProvenanceStatus): Severity | 'none' {
  switch (status) {
    case 'verified': return 'success';
    case 'partial': return 'warning';
    case 'pending': return 'info';
    case 'missing': return 'info';
    case 'failed': return 'critical';
    case 'restricted': return 'info';
    case 'not_applicable': return 'none';
    default: return 'none';
  }
}

/**
 * Formats the inline attestation text with band count when relevant.
 */
export function formatInlineAttestation(summary: ProvenanceSummary): string {
  const entry = PROVENANCE_COPY[summary.status as ProvenanceStatus];
  if (!entry) return '';
  const base = entry.inline;
  if (!base) return '';
  if (summary.status === 'verified') {
    return `${base} \u00b7 ${summary.verified_band_count} checks`;
  }
  if (summary.status === 'partial') {
    return `${base} \u00b7 ${summary.verified_band_count} of ${summary.total_band_count}`;
  }
  if (summary.status === 'failed') {
    return `${base} \u00b7 Review evidence`;
  }
  return base;
}

/**
 * Density rules: determines whether provenance should be shown for an event.
 * Prevents trust wallpaper by suppressing indicators on non-meaningful events.
 */
export function shouldShowProvenance(event: {
  isToolAction: boolean;
  isCompleted: boolean;
  isHeartbeat: boolean;
  isStatusUpdate: boolean;
  hasParentVerified?: boolean;
}): boolean {
  if (event.isHeartbeat || event.isStatusUpdate) return false;
  if (!event.isToolAction) return false;
  if (!event.isCompleted) return false;
  if (event.hasParentVerified) return false;
  return true;
}
